/**
 * DetailPageLayout - Orchestrator component for unified detail pages
 * 
 * Part of the Unified Detail Page Design System v1.0
 * 
 * This is the canonical layout all detail pages must use.
 * Structure:
 * 1. DetailHeader (sticky)
 * 2. Main content area (configurable via tabs or direct children)
 * 3. Optional side panels
 */

import React from 'react';
import DetailHeader from './DetailHeader';

/**
 * DetailPageLayout - Main orchestrator
 */
const DetailPageLayout = ({
  // Header props (passed through to DetailHeader)
  headerProps = {},
  
  // Layout configuration
  layout = 'default', // 'default' (with sidebar) | 'full' (no sidebar) | 'tabs'
  
  // Main content
  children,
  mainContent, // Alternative to children
  
  // Sidebar
  sidebarContent,
  sidebarWidth = 'w-80', // Tailwind width class
  
  // Loading/Error states
  loading = false,
  error = null,
  loadingComponent,
  errorComponent,
  
  // Container styling
  className = '',
  contentClassName = '',
  testId = 'detail-page',
}) => {
  // Loading state
  if (loading) {
    return loadingComponent || (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid={`${testId}-loading`}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return errorComponent || (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid={`${testId}-error`}>
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  const content = children || mainContent;
  
  return (
    <div className={`min-h-screen bg-gray-50 ${className}`} data-testid={testId}>
      {/* Header */}
      {headerProps && Object.keys(headerProps).length > 0 && (
        <DetailHeader {...headerProps} />
      )}
      
      {/* Content Area */}
      <div className={`p-6 max-w-7xl mx-auto ${contentClassName}`}>
        {layout === 'full' || !sidebarContent ? (
          // Full width layout
          <div className="space-y-6">
            {content}
          </div>
        ) : (
          // Layout with sidebar
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Column */}
            <div className="lg:col-span-2 space-y-6">
              {content}
            </div>
            
            {/* Sidebar */}
            <div className={`space-y-6 ${sidebarWidth}`}>
              {sidebarContent}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ActionBar - Quick actions row (used inside content)
 */
export const ActionBar = ({ 
  actions = [], // [{ label, icon, onClick, variant, testId }]
  className = '',
}) => (
  <div className={`flex items-center gap-2 flex-wrap ${className}`} data-testid="action-bar">
    {actions.filter(a => a.visible !== false).map((action, i) => {
      const Icon = action.icon;
      return (
        <button
          key={i}
          onClick={action.onClick}
          disabled={action.disabled}
          className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors
            ${action.variant === 'primary' 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }
            ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          data-testid={action.testId}
        >
          {Icon && <Icon className="w-4 h-4" />}
          {action.label}
        </button>
      );
    })}
  </div>
);

/**
 * ContentPanel - Standard content panel (used inside content)
 */
export const ContentPanel = ({
  title,
  icon: Icon,
  children,
  actions,
  locked = false,
  lockedMessage,
  className = '',
  testId,
}) => (
  <div className={`bg-white rounded-lg border ${className}`} data-testid={testId}>
    {/* Header */}
    {title && (
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <h3 className="font-medium text-gray-900">{title}</h3>
          {locked && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
              Locked
            </span>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    )}
    
    {/* Locked message */}
    {locked && lockedMessage && (
      <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-sm text-amber-800">
        {lockedMessage}
      </div>
    )}
    
    {/* Content */}
    <div className="p-4">
      {children}
    </div>
  </div>
);

export default DetailPageLayout;
